import { Clock3, RefreshCw } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { ProviderExtensionRequest, ProviderVenue } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import {
  extensionDeadline, isReviewableExtension, usePendingExtensionRequests, type ExtensionApprovalAuthority,
} from "./usePendingExtensionRequests";

export interface PendingExtensionRequestsProps {
  selectedVenue: ProviderVenue;
  authority: ExtensionApprovalAuthority;
  isOnline: boolean;
  notificationSessionId?: string | null;
  onSessionChanged?: (request: ProviderExtensionRequest) => Promise<void>;
}

export function PendingExtensionRequests({ selectedVenue, authority, isOnline, notificationSessionId, onSessionChanged }: PendingExtensionRequestsProps) {
  const { direction, resolvedLanguage, t } = useAppPreferences();
  const locale = resolvedLanguage === "ar" ? "ar-EG" : "en-US";
  const panelId = useId();
  const errorElement = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(Date.now());
  const queue = usePendingExtensionRequests({ venueId: selectedVenue.id, authority, isOnline, onSessionChanged });
  const online = isOnline && (typeof navigator === "undefined" || navigator.onLine);
  const rows = [...queue.requests, ...(queue.lastResult && !queue.requests.some((request) => request.quote.id === queue.lastResult?.quote.id) ? [queue.lastResult] : [])]
    .sort((left, right) => extensionDeadline(left) - extensionDeadline(right));
  const busy = queue.busy !== null || queue.refreshing;

  useEffect(() => { if (queue.error) errorElement.current?.focus(); }, [queue.error]);
  useEffect(() => {
    setNow(Date.now());
    if (!rows.length) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [rows.length]);

  function clock(value: string | null, timezone: string) {
    if (!value) return t("extensions.noPlannedEnd");
    if (!Number.isFinite(Date.parse(value))) return t("extensions.notAvailable");
    try {
      return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "medium", timeZone: timezone }).format(new Date(value));
    } catch { return value; }
  }
  function money(amount: number, currency: string) {
    if (!Number.isFinite(amount)) return t("extensions.notAvailable");
    return `${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${currency}`;
  }

  return (
    <section aria-labelledby={`${panelId}-heading`} className="ops-surface grid min-w-0 gap-4" dir={direction}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold" id={`${panelId}-heading`}>
            <Clock3 aria-hidden="true" size={19} /> {t("extensions.title")}
            {queue.allowed && <span className="ops-chip" data-tone="warning">{queue.requests.length}</span>}
          </h2>
          <p className="ops-copy mt-1 text-sm">{t("extensions.body")}</p>
          <p className="ops-copy mt-1 text-xs">{t("extensions.deadlineHint")}</p>
        </div>
        <button className="ops-button gap-2" data-variant="secondary" disabled={!queue.allowed || !online || busy} onClick={() => void queue.refresh()} style={{ minHeight: 44 }} type="button">
          <RefreshCw aria-hidden="true" size={16} /> {t("extensions.refresh")}
        </button>
      </header>
      {!queue.allowed && <p className="ops-copy text-sm">{t("extensions.membershipRequired")}</p>}
      {!online && <p className="ops-banner text-sm" data-tone="warning">{t("extensions.offline")}</p>}
      {queue.error && <div className="ops-banner text-sm" data-tone="danger" ref={errorElement} role="alert" tabIndex={-1}>{t(queue.error)}</div>}
      {queue.notice && <div className="ops-banner text-sm" data-tone={queue.notice === "extensions.notice.applied" ? "success" : "info"} role="status">{t(queue.notice)}</div>}
      <p aria-live="polite" className={queue.allowed && online && (!queue.ready || queue.refreshing) ? "ops-copy text-xs" : "sr-only"} role="status">
        {queue.allowed && online && (!queue.ready || queue.refreshing) ? t(queue.ready ? "extensions.updating" : "extensions.loading") : ""}
      </p>
      {queue.allowed && queue.ready && !queue.denied && !queue.error && !rows.length && <p className="ops-copy text-sm">{t("extensions.empty")}</p>}
      {rows.length > 0 && (
        <div className="grid max-h-[32rem] gap-3 overflow-y-auto pe-1">
          {rows.map((request) => {
            const { quote, session } = request;
            const pending = quote.phase === "pending_approval";
            const deadlinePassed = pending && extensionDeadline(request) <= now;
            const needsRefresh = queue.needsRefresh.includes(quote.id);
            const reviewable = isReviewableExtension(request, now);
            const disabled = !queue.allowed || queue.denied || !online || busy || needsRefresh || !reviewable;
            const phaseKey = needsRefresh ? "extensions.unconfirmed" : deadlinePassed ? "extensions.deadlinePassed" : `extensions.phase.${quote.phase}`;
            const operation = queue.busy?.quoteId === quote.id ? queue.busy.action : null;
            const highlighted = pending && notificationSessionId === session.id;
            return (
              <article aria-labelledby={`${panelId}-${quote.id}-heading`} className={`ops-record grid min-w-0 gap-3 ${highlighted ? "border-[var(--color-signal)]" : ""}`} data-extension-request-id={quote.id} key={quote.id}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="break-words text-sm font-semibold" id={`${panelId}-${quote.id}-heading`}><bdi>{session.unit_name}</bdi> · <bdi>{session.customer_name}</bdi></h3>
                    <p className="ops-copy break-all text-xs">{t("extensions.session")} <bdi>{session.id}</bdi></p>
                  </div>
                  <span className="ops-chip" data-tone={quote.phase === "applied" && !needsRefresh ? "success" : "warning"}>{t(phaseKey)}</span>
                </div>
                {highlighted && <p className="ops-copy text-xs">{t("extensions.targetHint")}</p>}
                <dl className="grid min-w-0 gap-3 text-sm sm:grid-cols-2">
                  <div><dt className="ops-copy text-xs">{t("extensions.currentEnd")}</dt><dd className="mt-1"><time dateTime={session.planned_end_at ?? undefined}>{clock(session.planned_end_at, quote.venue_timezone)}</time></dd></div>
                  <div><dt className="ops-copy text-xs">{t(pending ? "extensions.requestedEndPending" : "extensions.requestedEnd")}</dt><dd className="mt-1"><time dateTime={quote.proposed_end_at}>{clock(quote.proposed_end_at, quote.venue_timezone)}</time></dd></div>
                  <div><dt className="ops-copy text-xs">{t("extensions.additionalTime")}</dt><dd className="mt-1">{t("extensions.minutes", { minutes: quote.additional_minutes })}</dd></div>
                  <div><dt className="ops-copy text-xs">{t("extensions.additionalAmount")}</dt><dd className="mt-1 font-semibold"><bdi>{money(quote.additional_amount, quote.currency)}</bdi></dd></div>
                  <div><dt className="ops-copy text-xs">{t("extensions.currentTotal")}</dt><dd className="mt-1"><bdi>{money(quote.current_total_amount, quote.currency)}</bdi></dd></div>
                  <div><dt className="ops-copy text-xs">{t("extensions.proposedTotal")}</dt><dd className="mt-1"><bdi>{money(quote.proposed_total_amount, quote.currency)}</bdi></dd></div>
                  <div className="sm:col-span-2"><dt className="ops-copy text-xs">{t("extensions.deadline")}</dt><dd className="mt-1"><time dateTime={quote.approval_deadline ?? quote.expires_at}>{clock(quote.approval_deadline ?? quote.expires_at, quote.venue_timezone)}</time></dd></div>
                </dl>
                <p className="ops-copy text-xs">{t("extensions.timezone", { timezone: quote.venue_timezone })}</p>
                {pending && (deadlinePassed || needsRefresh || !reviewable) && <p className="ops-copy text-xs">{t(needsRefresh ? "extensions.refreshBeforeDecision" : deadlinePassed ? "extensions.expiredHint" : "extensions.changedHint")}</p>}
                <div className="flex flex-wrap gap-2">
                  {pending && <>
                    <button className="ops-button" data-variant="primary" disabled={disabled} onClick={() => void queue.decide(quote.id, "approve")} style={{ minHeight: 44 }} type="button">{t(operation === "approve" ? "extensions.approving" : "extensions.approve")}</button>
                    <button className="ops-button" data-variant="secondary" disabled={disabled} onClick={() => void queue.decide(quote.id, "reject")} style={{ minHeight: 44 }} type="button">{t(operation === "reject" ? "extensions.rejecting" : "extensions.reject")}</button>
                  </>}
                  <button className="ops-button" data-variant="secondary" disabled={!queue.allowed || !online || busy} onClick={() => void queue.refreshRequest(quote.id)} style={{ minHeight: 44 }} type="button">{t(operation === "refresh" ? "extensions.updating" : "extensions.refreshRequest")}</button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
